import React, { useCallback, useRef, useState } from 'react';
import { Box, ButtonV2, Flex, IconV2, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { majorToMinorUnits, minorToMajorUnits } from '@shared/utils/currency/minorUnits';
import type { PayoutAccountInfo } from './PayoutDialog.types';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { useMount } from '@shared/utils/hooks/useMount';
import { pxToRem } from '@withjoy/joykit/theme';

const clamp = (min: number, max: number, value: number) => Math.max(min, Math.min(max, value));

type AmountInputStepProps = Readonly<{
  payoutAccountInfo: PayoutAccountInfo;
  amountInMinorUnits: number;
  onAmountChange: (amountInMinorUnits: number) => void;
  onContinue: () => void;
  onPageViewed?: () => void;
}>;

export const AmountInputStep: React.FC<AmountInputStepProps> = ({ payoutAccountInfo, amountInMinorUnits, onAmountChange, onContinue, onPageViewed }) => {
  const { tq } = useTranslation('cashFundsAdmin');
  const { formatMinorUnitsCurrency, getCurrencySymbol } = useCurrencyFormatter();

  const currencyCode = payoutAccountInfo.balance?.available.currencyCode ?? payoutAccountInfo.currencyCode ?? DEFAULT_CURRENCY;
  const availableInMinorUnits = payoutAccountInfo.balance?.available.valueInMinorUnits ?? 0;

  const maxAmountInMinorUnits =
    payoutAccountInfo.payoutLimitInMinorUnits != null ? Math.min(payoutAccountInfo.payoutLimitInMinorUnits, availableInMinorUnits) : availableInMinorUnits;

  const minAmountInMinorUnits = payoutAccountInfo.standardPayoutFee?.freeThresholdInMinorUnits ?? 1;
  const maxAmountInMajorUnits = minorToMajorUnits(maxAmountInMinorUnits, currencyCode);
  const minAmountInMajorUnits = minorToMajorUnits(minAmountInMinorUnits, currencyCode);
  const showRangePill = payoutAccountInfo.payoutLimitInMinorUnits != null || minAmountInMinorUnits > 1;

  const [rawInput, setRawInput] = useState<string>(() => String(minorToMajorUnits(amountInMinorUnits, currencyCode)));
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useMount(() => {
    onPageViewed?.();
    inputRef.current?.focus();
    setIsFocused(true);
  });

  const parsedAmount = parseFloat(rawInput) || 0;
  const parsedAmountInMinorUnits = majorToMinorUnits(parsedAmount, currencyCode);
  const isAmountValid = maxAmountInMinorUnits > 0 && parsedAmountInMinorUnits >= minAmountInMinorUnits && parsedAmountInMinorUnits <= maxAmountInMinorUnits;

  const newBalanceInMinorUnits = Math.max(0, availableInMinorUnits - parsedAmountInMinorUnits);
  const currencySymbol = getCurrencySymbol(currencyCode);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const cleaned = raw.replace(/[^0-9.]/g, '');
      const dotIndex = cleaned.indexOf('.');
      const normalized =
        dotIndex === -1
          ? cleaned
          : `${cleaned.slice(0, dotIndex)}.${cleaned
              .slice(dotIndex + 1)
              .replace(/\./g, '')
              .slice(0, 2)}`;
      setRawInput(normalized);
      const parsed = parseFloat(normalized) || 0;
      const inMinorUnits = majorToMinorUnits(parsed, currencyCode);
      const clamped = clamp(minAmountInMinorUnits, maxAmountInMinorUnits, inMinorUnits);
      onAmountChange(clamped);
    },
    [currencyCode, minAmountInMinorUnits, maxAmountInMinorUnits, onAmountChange],
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    const parsed = parseFloat(rawInput) || 0;
    const clamped = clamp(minAmountInMajorUnits, maxAmountInMajorUnits, parsed);
    setRawInput(String(clamped));
    onAmountChange(majorToMinorUnits(clamped, currencyCode));
  }, [rawInput, minAmountInMajorUnits, maxAmountInMajorUnits, currencyCode, onAmountChange]);

  return (
    <Flex
      flexDirection="column"
      flex={1}
    >
      <TextV2
        textAlign="center"
        fontSize={pxToRem(24)}
        fontWeight={600}
        color="mono14"
        letterSpacing="-0.024em"
        lineHeight="1.3"
        marginBottom={6}
      >
        {tq`Transfer to bank`}
      </TextV2>

      <Flex
        flexDirection="column"
        alignItems="center"
        flex={1}
        justifyContent="center"
      >
        <TextV2
          textAlign="center"
          fontSize={pxToRem(15)}
          fontWeight={600}
          color="mono14"
          lineHeight="1.4"
          marginBottom={5}
        >
          {tq`How much would you like to transfer?`}
        </TextV2>

        <Flex
          flexDirection="column"
          alignItems="center"
        >
          <Flex
            borderStyle="solid"
            borderWidth="1px"
            borderColor={isFocused ? 'mono14' : 'mono3'}
            borderRadius={3}
            color="mono14"
            paddingX={5}
            paddingY={4}
            alignItems="center"
            justifyContent="center"
            width={pxToRem(280)}
            onClick={() => inputRef.current?.focus()}
            cursor="text"
          >
            <TextV2
              fontSize={pxToRem(36)}
              fontWeight={600}
              letterSpacing="-0.54px"
              lineHeight="1.2"
              as="span"
              flexShrink={0}
            >
              {currencySymbol}
            </TextV2>
            <Box
              as="input"
              ref={inputRef}
              value={rawInput}
              onChange={handleInputChange}
              onFocus={() => setIsFocused(true)}
              onBlur={handleBlur}
              inputMode="decimal"
              pattern="[0-9]*\.?[0-9]*"
              border="none"
              outline="none"
              background="transparent"
              minWidth="2ch"
              textAlign="center"
              fontSize={pxToRem(36)}
              fontWeight={600}
              lineHeight="1.2"
              letterSpacing="-0.54px"
            />
          </Flex>

          {showRangePill && (
            <Flex
              alignItems="center"
              backgroundColor="mono2"
              borderRadius={pxToRem(10)}
              paddingX={5}
              paddingY={pxToRem(10)}
              marginTop={pxToRem(18)}
              width={pxToRem(280)}
              gap={2}
            >
              <IconV2
                size="sm"
                color="primary7"
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.85"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="6.5"
                  />
                  <path d="M8 7.5v4" />
                  <circle
                    cx="8"
                    cy="5.25"
                    r="0.5"
                    fill="currentColor"
                    stroke="none"
                  />
                </svg>
              </IconV2>
              <TextV2
                fontSize={pxToRem(14)}
                color="mono11"
                lineHeight="1.4"
                textAlign="center"
              >
                {(() => {
                  const minFormatted = minAmountInMinorUnits > 1 ? formatMinorUnitsCurrency(minAmountInMinorUnits, currencyCode) : null;
                  const maxFormatted = payoutAccountInfo.payoutLimitInMinorUnits != null ? formatMinorUnitsCurrency(maxAmountInMinorUnits, currencyCode) : null;
                  if (minFormatted && maxFormatted) return tq`You can transfer from ${minFormatted} up to ${maxFormatted} now`;
                  if (minFormatted) return tq`You can transfer from ${minFormatted} now`;
                  return tq`You can transfer up to ${maxFormatted} now`;
                })()}
              </TextV2>
            </Flex>
          )}
        </Flex>

        <TextV2
          textAlign="center"
          fontSize={pxToRem(15)}
          color="mono10"
          lineHeight="1.4"
          marginTop={6}
        >
          {tq`New balance:`}{' '}
          <Box
            as="span"
            fontWeight={600}
            color="mono14"
          >
            {formatMinorUnitsCurrency(newBalanceInMinorUnits, currencyCode)}
          </Box>
        </TextV2>
      </Flex>

      <ButtonV2
        onClick={onContinue}
        size="lg"
        intent="neutral"
        shape="rounded"
        fullWidth
        marginTop={6}
        disabled={!isAmountValid}
      >
        {tq`Continue`}
      </ButtonV2>
    </Flex>
  );
};
